import React, { useRef } from "react";
import { HiMiniPencilSquare } from "react-icons/hi2";
import { useInbox } from "../../contexts/inbox";
import { getThreadDisplayInfo, getThreadFishId } from "../../utils/threadDisplay";
import { useFishFlowCategories } from "../../hooks/useFishFlowCategories";
import { useVisibleRange } from "../../hooks/useVisibleRange";
import { FlowCategoryIcons } from "../FlowCategoryIcons";

/** Rows above and below the visible ones whose flow categories are loaded. */
const PREFETCH_BUFFER_ROWS = 5;
/** Rows treated as visible before the first intersection observation. */
const INITIAL_ROW_COUNT = 15;

const formatTimestamp = (timestamp) => {
  const now = new Date();
  const messageDate = new Date(timestamp);

  // Today
  if (messageDate.toDateString() === now.toDateString()) {
    return messageDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  // This week
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7);
  if (messageDate > weekAgo) {
    return messageDate.toLocaleDateString("en-US", { weekday: "long" });
  }

  // Older
  return messageDate.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
};

const InboxThreadRow = ({
  thread,
  index,
  selectedChannel,
  isSelected,
  loadCategories,
  onSelect,
}) => {
  const categories = useFishFlowCategories(
    getThreadFishId(thread, selectedChannel),
    loadCategories,
  );
  const displayInfo = getThreadDisplayInfo(thread, selectedChannel);

  const hasUnread =
    selectedChannel === 'APP'
      ? thread.unreadCount > 0
      : selectedChannel === 'SMS'
        ? (thread.SMSThread || []).some(
            (m) => m.sender && m.read === false,
          )
        : false;

  return (
    <div
      data-index={index}
      className={`p-4 cursor-pointer ${
        isSelected
          ? 'bg-gray-100'
          : hasUnread
            ? 'bg-blue-50 hover:bg-blue-100'
            : 'hover:bg-gray-50'
      }`}
      onClick={() => onSelect(thread)}
    >
      <div className="flex justify-between items-start">
        <div className="flex min-w-0 items-center gap-2">
          {hasUnread && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-rose-400"
              aria-label="Unread"
            />
          )}
          <span className="truncate font-semibold">{displayInfo.name}</span>
          <FlowCategoryIcons categories={categories} />
        </div>
        <span className="text-sm text-gray-500">
          {displayInfo.timestamp ? formatTimestamp(displayInfo.timestamp) : ''}
        </span>
      </div>
      <p className="text-sm text-gray-600 mt-1 truncate">
        {displayInfo.lastMessageBody}
      </p>
      {selectedChannel === 'APP' && thread.unreadCount > 0 && (
        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
          {thread.unreadCount} unread
        </span>
      )}
    </div>
  );
};

const InboxThreadList = () => {
  const {
    threads,
    selectedFishThread,
    searchQuery,
    setSearchQuery,
    handleThreadSelect,
    handleNewThreadButton,
    selectedGroupId,
    inboxId,
    selectedChannel
  } = useInbox();

  const listRef = useRef(null);
  const visibleRange = useVisibleRange(listRef, threads);
  const windowStart = (visibleRange?.first ?? 0) - PREFETCH_BUFFER_ROWS;
  const windowEnd =
    (visibleRange?.last ?? INITIAL_ROW_COUNT) + PREFETCH_BUFFER_ROWS;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col border-r border-gray-200">
      {/* New message icon row - only show when not in "All inboxes" view */}
      {(inboxId) && (
        <div className="px-4 flex justify-end">
          <button
            onClick={handleNewThreadButton}
            className="p-2 rounded hover:bg-gray-200"
          >
            <HiMiniPencilSquare className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      )}

      {/* Search bar */}
      <div className="px-4 pb-4 border-b border-gray-200">
        <input
          type="text"
          placeholder="Search messages..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Thread list */}
      <div ref={listRef} className="flex-1 overflow-y-auto no-scrollbar">
        {threads.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No {selectedChannel} conversations found
          </div>
        ) : (
          threads.map((thread, index) =>
            getThreadDisplayInfo(thread, selectedChannel).hasMessages ? (
              <InboxThreadRow
                key={`${thread.id}-${thread.inboxId || 'unknown'}`}
                thread={thread}
                index={index}
                selectedChannel={selectedChannel}
                isSelected={
                  selectedFishThread?.id === thread.id &&
                  selectedFishThread?.inboxId === thread.inboxId
                }
                loadCategories={index >= windowStart && index <= windowEnd}
                onSelect={handleThreadSelect}
              />
            ) : null,
          )
        )}
      </div>
    </div>
  );
};

export default InboxThreadList;
